/**
 * Game modes, loaded lazily per level (the "Level module" stage, A-29 / S-7).
 *
 * Levels name a game mode in `Zone.gameMode`. A mode's code is a separate chunk,
 * loaded when a level that uses it opens (the level transition's "Level module"
 * stage), so Play in Editor starts without a download.
 */

import type { GameModeModule } from "./GameMode";

export type GameModeLoader = () => Promise<GameModeModule>;

export class GameModeRegistry {
  private readonly loaders = new Map<string, GameModeLoader>();
  private readonly loaded = new Map<string, Promise<GameModeModule>>();

  register(id: string, load: GameModeLoader): void {
    if (this.loaders.has(id)) throw new Error(`Game mode '${id}' is already registered`);
    this.loaders.set(id, load);
  }

  has(id: string): boolean {
    return this.loaders.has(id);
  }

  /** Loads a mode once; later calls share the same module. */
  load(id: string): Promise<GameModeModule> {
    const loader = this.loaders.get(id);
    if (!loader) return Promise.reject(new Error(`Game mode '${id}' is not registered`));

    let pending = this.loaded.get(id);
    if (!pending) {
      pending = loader();
      this.loaded.set(id, pending);
    }
    return pending;
  }
}
